import { Dispatch, useCallback, useEffect, useState } from 'react'
import { Criteria_Group_Config_Set_Input, Mutation_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  CriteriaConfigurationCriteriaGroupsForm,
  CriteriaGroupFormValues,
} from 'src/screens/administration/configuration-management/criteria-configuration/criteria-group/edit/CriteriaConfigurationCriteriaGroupsForm'
import { insertCriteriaGroupConfig } from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import { CreateButton } from 'src/shared/button/Buttons'
import { CONFIGURATION_STATUS, PROCESS } from 'src/shared/constants/constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

const defaultInitialValues: CriteriaGroupFormValues = {
  enName: '',
  deName: '',
  frName: '',
  itName: '',
  status: CONFIGURATION_STATUS.ACTIVE,
  processAlloc: PROCESS.PF_KAP,
  criteriaTypes: [],
}

export const CriteriaConfigurationCriteriaGroupsCreatePage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()

  const [initialValues, setInitialValues] = useState<CriteriaGroupFormValues | null>(null)

  useEffect(() => {
    setInitialValues(defaultInitialValues)
  }, [])

  const onBackHandler = useCallback(() => {
    navigate(ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationCriteriaGroups.params({}))
  }, [navigate])

  const onCreate = async () => {
    document.getElementById('criteria-group-config-form')?.dispatchEvent(
      new Event('submit', {
        cancelable: true,
        bubbles: true,
      }),
    )
  }

  const handleSubmitLocal = useCallback(
    async (values: CriteriaGroupFormValues, setFormDirty: Dispatch<boolean>) => {
      const criteriaGroupInsertInput: Criteria_Group_Config_Set_Input = {
        names: { de: values.deName, en: values.enName, fr: values.frName, it: values.itName },
        group_processes: [values.processAlloc],
        group_active: values.status == CONFIGURATION_STATUS.ACTIVE,
        code: crypto.randomUUID(),
        criteria_types: values.criteriaTypes,
      }

      const { data, error } = await urqlClient
        .mutation<{
          insert_criteria_group_config: Mutation_Root['insert_criteria_group_config']
        }>(insertCriteriaGroupConfig, {
          criteriaGroupInsertInput,
        })
        .toPromise()

      if (!error && data?.insert_criteria_group_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setFormDirty(false)
        onBackHandler()
      } else {
        notificationService.operationFailed()
      }
    },
    [notificationService, urqlClient, onBackHandler],
  )

  return (
    <ScreenLayout
      title={getMessage('label.configuration.criteria.group.create')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={<CreateButton origin="header" onClick={onCreate} />}
    >
      <PageLayout>
        <>
          {initialValues && (
            <CriteriaConfigurationCriteriaGroupsForm
              mode="CREATE"
              onSubmit={handleSubmitLocal}
              initialValues={defaultInitialValues}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
